import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  RefreshCw, 
  Database, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Copy,
  ExternalLink,
  Menu,
  Users,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/config';


const DatabaseSync = () => {
  const [status, setStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const getToken = () => localStorage.getItem('hw_token');

  const checkStatus = async () => {
    setChecking(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sync-status`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        toast.error('Errore nel controllo stato');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setChecking(false);
    }
  };

  const runSync = async () => {
    setLoading(true);
    setSyncResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/sync-missing-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncResult(data);
        
        if (data.summary?.success) {
          toast.success('Sincronizzazione completata!');
          // Refresh status after sync
          await checkStatus();
        } else {
          toast.error(data.summary?.message || 'Errore nella sincronizzazione');
        }
      } else {
        toast.error('Errore nella sincronizzazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copiato!');
  };

  useEffect(() => {
    checkStatus();
  }, []);

  return (
    <div className="p-6 space-y-6" data-testid="database-sync-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sincronizzazione Database</h1>
          <p className="text-gray-500 mt-1">
            Sincronizza i dati mancanti e genera i link di test per i collaboratori
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={checkStatus}
          disabled={checking}
          data-testid="refresh-status-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
          Aggiorna Stato
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Menu Configuration Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Menu className="h-4 w-4" />
              Configurazione Menu
            </CardTitle>
          </CardHeader>
          <CardContent>
            {checking ? (
              <div className="animate-pulse h-20 bg-gray-100 rounded" />
            ) : status?.menu_configurations?.admin ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Voci menu admin</span>
                  <Badge variant="secondary">{status.menu_configurations.admin.total_items || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Anagrafica Ospiti</span>
                  {status.menu_configurations.admin.has_guest_documents ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Flussi Turistici</span>
                  {status.menu_configurations.admin.has_tourism_flows ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-red-500">Menu admin non configurato</div>
            )}
          </CardContent>
        </Card>

        {/* Test Tokens Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <LinkIcon className="h-4 w-4" />
              Token di Test
            </CardTitle>
          </CardHeader>
          <CardContent>
            {checking ? (
              <div className="animate-pulse h-20 bg-gray-100 rounded" />
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Token creati</span>
                  <Badge variant={status?.test_tokens?.complete ? "default" : "destructive"}>
                    {status?.test_tokens?.count || 0} / {status?.test_tokens?.expected || 5}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Stato</span>
                  {status?.test_tokens?.complete ? (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Completo
                    </span>
                  ) : (
                    <span className="text-sm text-orange-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Incompleto
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Collections Count */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Database className="h-4 w-4" />
              Dati Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            {checking ? (
              <div className="animate-pulse h-20 bg-gray-100 rounded" />
            ) : (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Prenotazioni</span>
                  <span className="font-medium">{status?.collections_count?.bookings || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Immobili</span>
                  <span className="font-medium">{status?.collections_count?.properties || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Documenti Ospiti</span>
                  <span className="font-medium">{status?.collections_count?.guest_documents || 0}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sync Button */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Esegui Sincronizzazione
          </CardTitle>
          <CardDescription>
            Clicca il pulsante per aggiungere le voci di menu mancanti e generare i link di test per i collaboratori.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={runSync} 
            disabled={loading}
            size="lg"
            className="w-full md:w-auto"
            data-testid="run-sync-btn"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Sincronizzazione in corso...
              </>
            ) : (
              <>
                <Database className="h-4 w-4 mr-2" />
                Sincronizza Dati Mancanti
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Sync Results */}
      {syncResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {syncResult.summary?.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              Risultato Sincronizzazione
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Menu Items Added */}
            {syncResult.menu_items_added?.length > 0 && (
              <Alert>
                <Menu className="h-4 w-4" />
                <AlertTitle>Voci Menu Aggiunte</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside mt-2">
                    {syncResult.menu_items_added.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Test Tokens */}
            {syncResult.test_tokens_created?.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Link di Test per Collaboratori
                </h3>
                <div className="space-y-2">
                  {syncResult.test_tokens_created.map((token, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                          Collaboratore #{token.collaboratore}
                          {token.status && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {token.status}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-1">
                          {token.link}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(token.link)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(token.link, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {syncResult.errors?.length > 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Errori</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside mt-2">
                    {syncResult.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Summary */}
            <div className="pt-4 border-t">
              <p className={`font-medium ${syncResult.summary?.success ? 'text-green-600' : 'text-red-600'}`}>
                {syncResult.summary?.message}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800 text-lg">Istruzioni</CardTitle>
        </CardHeader>
        <CardContent className="text-blue-700 text-sm space-y-2">
          <p>1. Clicca "Sincronizza Dati Mancanti" per aggiungere le configurazioni mancanti</p>
          <p>2. Dopo la sincronizzazione, fai <strong>hard refresh</strong> (Ctrl+Shift+R) per vedere il menu aggiornato</p>
          <p>3. I link di test per i collaboratori saranno validi per 30 giorni</p>
          <p>4. Condividi ogni link con un collaboratore diverso per testare il check-in ospiti</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabaseSync;
